/**
 * Sortable article row for drag-and-drop reordering.
 * Use within DndContext and SortableContext (verticalListSortingStrategy).
 */

import React from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { DragOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import type { KBArticle } from '@municipality/types';

function getDisplayTitle(
  translations: Record<string, { title?: string }>,
  primaryLanguage: string
): string {
  const primary = translations[primaryLanguage]?.title;
  if (primary) return primary;
  const en = translations.en?.title;
  if (en) return en;
  const first = Object.values(translations)[0]?.title;
  return first ?? '';
}

export interface SortableArticleRowProps {
  article: KBArticle;
  categoryName?: string;
  onEdit?: (article: KBArticle) => void;
  onDelete?: (article: KBArticle) => void;
  /** When false, hides drag handle (read-only mode). */
  dragEnabled?: boolean;
}

export const SortableArticleRow: React.FC<SortableArticleRowProps> = ({
  article,
  categoryName,
  onEdit,
  onDelete,
  dragEnabled = true,
}) => {
  const { t } = useTranslation(['knowledgeBase', 'common']);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: article.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const displayTitle = getDisplayTitle(
    article.translations,
    article.languageSettings.primaryLanguage
  );

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: isDragging ? '#fafafa' : 'transparent',
        borderRadius: '6px',
        margin: '4px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: isDragging ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none',
      }}
    >
      {dragEnabled ? (
        <div
          style={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            padding: '4px',
            borderRadius: '4px',
            backgroundColor: isDragging ? '#f0f0f0' : 'transparent',
          }}
          {...attributes}
          {...listeners}
        >
          <DragOutlined style={{ color: '#8c8c8c', fontSize: '16px' }} />
        </div>
      ) : (
        <div style={{ width: 24 }} />
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text strong style={{ fontSize: '16px' }}>
          {displayTitle || t('articles.titleField')}
        </Typography.Text>
        <div style={{ marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Tag color={article.status === 'published' ? 'green' : 'default'}>
            {article.status === 'published'
              ? t('knowledgeBase:articles.published')
              : t('knowledgeBase:articles.draft')}
          </Tag>
          {categoryName && (
            <Typography.Text type="secondary">{categoryName}</Typography.Text>
          )}
        </div>
      </div>

      <Space size="small">
        {onEdit && (
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(article)}
            aria-label={t('common:common.edit')}
          />
        )}
        {onDelete && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(article)}
            aria-label={t('common:common.delete')}
          />
        )}
      </Space>
    </div>
  );
};
